import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full border border-[#DCDCD6] bg-[#F4F4F0] px-4 py-3 text-base text-[#111111] placeholder:text-[#999999] focus:border-[#111111] focus:outline-none transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "", message: "" });
  const [lawnSign, setLawnSign] = useState(false);
  const [address, setAddress] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const message = lawnSign
      ? `LAWN SIGN REQUEST — Address: ${address}\n\n${form.message}`
      : form.message;
    try {
      await axios.post(`${API}/contact`, {
        name: `${form.first} ${form.last}`.trim(),
        email: form.email,
        company: form.phone || null,
        message,
      });
      setSent(true);
      setForm({ first: "", last: "", email: "", phone: "", message: "" });
      setLawnSign(false);
      setAddress("");
      toast.success(
        lawnSign
          ? "Lawn sign request received — the campaign will be in touch."
          : "Message received — Joel's office will be in touch."
      );
    } catch (err) {
      toast.error("Something went wrong. Please email joel@joelanthonybruce.ca directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-36 border-t border-[#DCDCD6] px-6 py-24 md:scroll-mt-24 md:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#666666]">Get in Touch</p>
        <h2 className="mt-4 font-serif text-3xl font-light leading-tight tracking-tight md:text-5xl">
          CONTACT JOEL OR REQUEST A LAWN SIGN
        </h2>
        <p className="mt-6 text-base leading-relaxed text-[#666666]">
          Joel reads every message personally. Whether you have a question, a
          concern about the neighbourhood, or you'd like a lawn sign for the
          campaign — get in touch. You can also contact Joel by phone or text
          at{" "}
          <a
            href="tel:+16132174104"
            className="border-b border-[#111111] font-semibold text-[#111111] transition-colors duration-300 hover:text-[#666666]"
            data-testid="contact-phone-link"
          >
            (613) 217-4104
          </a>
          .
        </p>
        <form onSubmit={submit} className="mt-10 flex flex-col gap-4" data-testid="contact-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              required
              value={form.first}
              onChange={update("first")}
              placeholder="First Name"
              className={inputClass}
              data-testid="contact-first-name-input"
            />
            <input
              required
              value={form.last}
              onChange={update("last")}
              placeholder="Last Name"
              className={inputClass}
              data-testid="contact-last-name-input"
            />
          </div>
          <input
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            placeholder="Email Address"
            className={inputClass}
            data-testid="contact-email-input"
          />
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="Phone (optional)"
            className={inputClass}
            data-testid="contact-phone-input"
          />
          <textarea
            required
            rows={5}
            value={form.message}
            onChange={update("message")}
            placeholder="Your message"
            className={`${inputClass} resize-none`}
            data-testid="contact-message-input"
          />
          <div className="border border-[#DCDCD6] bg-[#EBEBE6] p-5">
            <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold" data-testid="lawn-sign-toggle">
              <input
                type="checkbox"
                checked={lawnSign}
                onChange={(e) => setLawnSign(e.target.checked)}
                className="h-4 w-4 accent-[#111111]"
                data-testid="lawn-sign-checkbox"
              />
              Yes — I'd like a lawn sign for the campaign
            </label>
            {lawnSign && (
              <input
                required
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Street address for sign delivery"
                className={`${inputClass} mt-4 bg-[#F4F4F0]`}
                data-testid="lawn-sign-address-input"
              />
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-6">
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="flex items-center gap-3 bg-[#111111] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#F4F4F0] transition-transform duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  Sending <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                "Send Message →"
              )}
            </button>
            {sent && (
              <p
                className="text-xs font-semibold uppercase tracking-[0.2em] text-[#666666]"
                data-testid="contact-success-message"
              >
                Received — talk soon.
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};
